import React, { useState } from 'react';
import {
    Alert,
    Autocomplete,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    Switch,
    TextField,
} from '@mui/material';
import { CARD_KINDS, toRequest } from './loginPageCards.constants';
import { createLoginPageCard, updateLoginPageCard } from './loginPageCards.service';

const TITLE_MAX = 120;
const BODY_MAX = 500;
const LABEL_MAX = 40;

const EMPTY = {
    kind: 'NEWS',
    title: '',
    body: '',
    linkUrl: '',
    linkLabel: '',
    imageUrl: '',
    personas: [],
    hideIfAppUrl: '',
    active: true,
    sortOrder: 0,
    startsOn: '',
    endsOn: '',
};

const isWebUrl = (value) => !value || /^https?:\/\/\S+$/i.test(value.trim());

/**
 * Add or edit one card. The backend checks the same rules and answers 400 with a message, which is
 * shown above the form.
 */
function LoginPageCardDialog({ card, personas, onClose, onSaved }) {
    const isNew = !card.id;
    const [draft, setDraft] = useState(() => ({
        ...EMPTY,
        ...Object.fromEntries(Object.entries(card).filter(([, v]) => v !== null && v !== undefined)),
    }));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const set = (field) => (e) => setDraft((d) => ({ ...d, [field]: e.target.value }));

    const problems = {
        title: !draft.title.trim() ? 'A title is required' : null,
        linkUrl: isWebUrl(draft.linkUrl) ? null : 'A web address starting with https://',
        imageUrl: isWebUrl(draft.imageUrl) ? null : 'A web address starting with https://',
        sortOrder: Number.isInteger(draft.sortOrder) ? null : 'A whole number',
        endsOn: draft.startsOn && draft.endsOn && draft.endsOn < draft.startsOn ? 'Ends before it starts' : null,
    };
    const valid = Object.values(problems).every((p) => !p);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const request = toRequest({ ...draft, title: draft.title.trim() });
            onSaved(isNew ? await createLoginPageCard(request) : await updateLoginPageCard(card.id, request));
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={() => (saving ? undefined : onClose())} fullWidth maxWidth="sm">
            <DialogTitle>{isNew ? 'Add card' : 'Edit card'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {error ? <Alert severity="error">{error}</Alert> : null}
                    <TextField select label="Section" value={draft.kind} onChange={set('kind')}>
                        {CARD_KINDS.map((k) => (
                            <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        label="Title"
                        required
                        value={draft.title}
                        onChange={set('title')}
                        error={Boolean(problems.title) && draft.title !== ''}
                        helperText={`${draft.title.length}/${TITLE_MAX}`}
                        slotProps={{ htmlInput: { maxLength: TITLE_MAX } }}
                    />
                    <TextField
                        label="Text"
                        multiline
                        minRows={3}
                        value={draft.body}
                        onChange={set('body')}
                        helperText={`${draft.body.length}/${BODY_MAX}`}
                        slotProps={{ htmlInput: { maxLength: BODY_MAX } }}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Link (optional)"
                            placeholder="https://"
                            value={draft.linkUrl}
                            onChange={set('linkUrl')}
                            error={Boolean(problems.linkUrl)}
                            helperText={problems.linkUrl || 'Where the card\'s button goes'}
                            sx={{ flex: 2 }}
                        />
                        <TextField
                            label="Button text"
                            placeholder="Learn more"
                            value={draft.linkLabel}
                            onChange={set('linkLabel')}
                            disabled={!draft.linkUrl}
                            slotProps={{ htmlInput: { maxLength: LABEL_MAX } }}
                            sx={{ flex: 1 }}
                        />
                    </Stack>
                    <TextField
                        label="Image (optional)"
                        placeholder="https://"
                        value={draft.imageUrl}
                        onChange={set('imageUrl')}
                        error={Boolean(problems.imageUrl)}
                        helperText={problems.imageUrl || ' '}
                    />
                    <Autocomplete
                        multiple
                        options={personas}
                        value={draft.personas}
                        onChange={(_e, value) => setDraft((d) => ({ ...d, personas: value }))}
                        renderInput={(params) => (
                            <TextField {...params} label="Shown to personas" helperText="Leave empty to show it to everyone" />
                        )}
                    />
                    <TextField
                        label="Hide for users who already have this app (URL)"
                        placeholder="https://"
                        value={draft.hideIfAppUrl}
                        onChange={set('hideIfAppUrl')}
                        helperText="E.g. a card offering eK Flux, with eK Flux's address here, is not shown to people who already use eK Flux"
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                        <TextField
                            label="Order"
                            type="number"
                            value={Number.isNaN(draft.sortOrder) ? '' : draft.sortOrder}
                            onChange={(e) => setDraft((d) => ({ ...d, sortOrder: e.target.value === '' ? NaN : Number(e.target.value) }))}
                            error={Boolean(problems.sortOrder)}
                            helperText={problems.sortOrder || 'Lower shows first'}
                            slotProps={{ htmlInput: { step: 1 } }}
                        />
                        <TextField
                            label="Show from"
                            type="date"
                            value={draft.startsOn}
                            onChange={set('startsOn')}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                        <TextField
                            label="Show until"
                            type="date"
                            value={draft.endsOn}
                            onChange={set('endsOn')}
                            error={Boolean(problems.endsOn)}
                            helperText={problems.endsOn || ' '}
                            slotProps={{ inputLabel: { shrink: true } }}
                        />
                    </Stack>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={draft.active}
                                onChange={(e) => setDraft((d) => ({ ...d, active: e.target.checked }))}
                            />
                        }
                        label="Active"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button disabled={saving} onClick={onClose}>Cancel</Button>
                <Button variant="contained" disabled={!valid || saving} onClick={save}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

export default LoginPageCardDialog;
